import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Flag, Mail } from "lucide-react";
import { reportPlace, getMailtoFallback } from "@/lib/reporting/reportPlace";

interface ReportDialogProps {
  placeId?: string;
  placeName: string;
  placeSlug: string;
}

const REPORT_TYPES = [
  { value: "wrong_address", label: "Fel adress" },
  { value: "closed", label: "Stängt" },
  { value: "wrong_link", label: "Fel länk" },
  { value: "other", label: "Annat" },
] as const;

const ReportDialog = ({ placeId, placeName, placeSlug }: ReportDialogProps) => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<string>("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!type) {
      toast({
        title: "Välj typ av fel",
        description: "Du måste välja vad som är fel innan du skickar.",
        variant: "destructive",
      });
      return;
    }

    if (!message.trim() || message.trim().length < 3) {
      toast({
        title: "Skriv ett meddelande",
        description: "Beskriv kort vad som är fel (minst 3 tecken).",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    setShowFallback(false);

    const result = await reportPlace({
      placeId,
      placeSlug,
      placeName,
      reportType: type,
      message: message.trim(),
    });

    setSubmitting(false);

    if (result.success) {
      toast({
        title: "Tack för din hjälp!",
        description: "Vi har tagit emot din rapport och kollar på det.",
      });
      setOpen(false);
      setType("");
      setMessage("");
    } else {
      toast({
        title: "Kunde inte skicka just nu",
        description: result.error || "Prova igen eller skicka via e-post.",
        variant: "destructive",
      });
      setShowFallback(true);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setType("");
      setMessage("");
      setShowFallback(false);
    }
  };

  const mailtoUrl = getMailtoFallback({
    placeSlug,
    placeName,
    reportType: type || "other",
    message: message || "(inget meddelande)",
  });

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="text-muted-foreground">
          <Flag className="h-4 w-4 mr-2" />
          Rapportera fel
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Rapportera fel</DialogTitle>
          <DialogDescription>
            Hjälp oss hålla informationen om {placeName} uppdaterad.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Honeypot field - hidden from users */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            style={{ position: "absolute", left: "-9999px", opacity: 0 }}
            aria-hidden="true"
          />
          
          <div className="space-y-2">
            <Label htmlFor="report-type">Vad stämmer inte?</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger id="report-type">
                <SelectValue placeholder="Välj typ av fel" />
              </SelectTrigger>
              <SelectContent>
                {REPORT_TYPES.map((rt) => (
                  <SelectItem key={rt.value} value={rt.value}>
                    {rt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="report-message">Beskriv felet</Label>
            <Textarea
              id="report-message"
              placeholder="Berätta mer om vad som är fel..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              maxLength={1000}
            />
            <p className="text-xs text-muted-foreground text-right">
              {message.length}/1000
            </p>
          </div>
          
          <div className="flex flex-col gap-3">
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setOpen(false)}
              >
                Avbryt
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? "Skickar..." : "Skicka"}
              </Button>
            </div>
            
            {showFallback && (
              <div className="text-center pt-2 border-t">
                <a
                  href={mailtoUrl}
                  className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  Skicka via e-post istället
                </a>
              </div>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReportDialog;